import { useQuery } from '@apollo/client/react';
import { filter } from 'lodash';
import { useLayoutEffect, useMemo, useState } from 'react';

import CurrentUserContext from '.';

import { GetCurrentUserDocument, UserSettingsDocument } from '@/graphql/generated/graphql';
import { rootRouteModeVar, setRootRouteMode } from '@/router/reactiveRoutes';

interface CurrentUserContextProviderProps {
  children: React.ReactNode;
}

const CurrentUserContextProvider: React.FC<CurrentUserContextProviderProps> = ({ children }) => {
  const [modeResolved, setModeResolved] = useState(false);
  const { data, refetch, loading } = useQuery(GetCurrentUserDocument, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const currentUser = data?.fetchMyData;

  const { data: userSettingsData } = useQuery(UserSettingsDocument, {
    skip: !currentUser,
  });

  const userSettings = userSettingsData?.userSettings;

  let dashboardLayout =
    filter(currentUser?.dashboardLayout?.split(',') || [], (dl: string) => dl !== 'Feedbacks') || [];

  if (!currentUser?.welcomeChecklistCompleted) {
    dashboardLayout = ['Welcome', ...dashboardLayout];
  }

  const dateTimeFormatObject = () => {
    const formatFromQuery = JSON.parse(userSettings?.dateTimePreference || '{"date":"DD/MM/YYYY","time":"HH:mm"}');

    if (!formatFromQuery.date && formatFromQuery.time) {
      return { date: 'DD/MM/YYYY', time: formatFromQuery.time };
    }

    if (!formatFromQuery.time && formatFromQuery.date) {
      return { date: formatFromQuery.date, time: 'HH:mm' };
    }

    return formatFromQuery;
  };

  useLayoutEffect(() => {
    if (!data?.fetchMyData) return;
    const currentMode = rootRouteModeVar();
    if (data.fetchMyData.isSuperAdmin && currentMode !== 'super') {
      setRootRouteMode('super');
    } else if (!data.fetchMyData.isSuperAdmin && currentMode === 'super') {
      setRootRouteMode('manager');
    }
    setModeResolved(true);
  }, [data]);

  const context = useMemo(
    () => ({
      currentUser,
      currentCompany: currentUser?.defaultCompany,
      refetchCurrentUser: refetch,
      loadingCurrentUser: loading || !modeResolved,
      dashboardLayout,
      dateTimePreference: dateTimeFormatObject(),
    }),
    [currentUser, loading, modeResolved, refetch, userSettings],
  );

  return <CurrentUserContext.Provider value={context}>{children}</CurrentUserContext.Provider>;
};

export default CurrentUserContextProvider;
